import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Briefcase, Search, Shield,
  Award, Building, BookOpen, FileText, Landmark, GitMerge, Compass
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { AnimateIn } from '../components/Layout';
import { services } from '../data/mock';

const iconMap = {
  Briefcase, Search, Shield, Award, Building, BookOpen, FileText, Landmark, GitMerge, Compass,
};

const ServiceCard = ({ service, idx }) => {
  const Icon = iconMap[service.icon];

  return (
    <AnimateIn delay={idx * 80}>
      <div className="bg-white border border-gray-100 rounded-sm overflow-hidden hover:shadow-md hover:border-[#C9A646]/20 transition-all duration-300">
        <div className="p-8">
          <div className="flex items-start gap-5 mb-5">
            <div className="w-12 h-12 rounded-full bg-[#C9A646]/10 flex items-center justify-center shrink-0">
              {Icon && <Icon size={22} className="text-[#C9A646]" />}
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold text-[#0B1F3A] mb-2">{service.title}</h3>
              <p className="text-gray-500 font-body text-sm leading-relaxed">{service.description}</p>
            </div>
          </div>
          <div className="pl-[68px]">
            <div className="border-t border-gray-100 pt-5">
              <p className="text-gray-600 font-body text-sm leading-relaxed mb-4">{service.details}</p>
              <Link to="/consultation" className="inline-flex items-center gap-2 text-[#C9A646] font-body font-semibold text-sm hover:gap-3 transition-all">
                Get this service <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </AnimateIn>
  );
};

const Services = () => {
  return (
    <div className="pt-20">
      {/* Page Hero */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimateIn>
            <span className="text-[#C9A646] font-body font-semibold text-sm tracking-[0.25em] uppercase mb-4 block">Services</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-[#0B1F3A] leading-tight mb-6">
              Comprehensive Labour<br />Law Solutions
            </h1>
            <p className="text-gray-500 font-body text-lg max-w-2xl leading-relaxed mb-6">
              End-to-end compliance services designed to protect your organization and ensure regulatory adherence across all aspects of employment law.
            </p>
            <div className="w-16 h-[2px] bg-[#C9A646]" />
          </AnimateIn>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {services.map((service, idx) => (
              <ServiceCard key={service.id} service={service} idx={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-[#0B1F3A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <AnimateIn>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">Need a Custom Solution?</h2>
            <p className="text-gray-400 font-body text-lg max-w-2xl mx-auto mb-10">
              Every organization has unique compliance needs. Let's discuss your specific requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/consultation">
                <Button className="bg-[#C9A646] hover:bg-[#B8953B] text-[#0B1F3A] font-semibold font-body rounded-sm px-8 h-12 text-base w-full sm:w-auto">
                  Schedule Consultation
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#0B1F3A] font-body rounded-sm px-8 h-12 text-base w-full sm:w-auto">
                  Contact Me
                </Button>
              </Link>
            </div>
          </AnimateIn>
        </div>
      </section>
    </div>
  );
};

export default Services;
